import type { InvoiceSchema } from "@/stores/invoice";
import type { PosSchema } from "@/stores/pos";

type Translations = Record<string, any> | null | undefined;

export interface PrintParams {
  data: {
    // organization
    orgId: string;
    orgDisplayName: PosSchema["pointOfSale"]["organization"]["displayName"];
    orgDisplayNameTranslations: Translations;
    orgTaxNumber: PosSchema["pointOfSale"]["organization"]["taxNumber"];
    orgPhone: PosSchema["pointOfSale"]["organization"]["phone"];
    orgAddress: PosSchema["pointOfSale"]["organization"]["address"];
    orgAddressTranslations: Translations;
    orgCountry: PosSchema["pointOfSale"]["organization"]["country"];
    posSettings?: PosSchema["pointOfSale"]["organization"]["posSettings"];

    // store
    branchName: PosSchema["pointOfSale"]["inventoryStore"]["name"];
    branchNameTranslations: Translations;
    branchAddress: PosSchema["pointOfSale"]["inventoryStore"]["address"];
    branchAddressTranslations: Translations;

    // invoice
    invoiceReference: InvoiceSchema["reference"];
    createdByName: string;

    customerName: string;
    customerNameTranslations: Translations;
    customerTaxNumber?: string;
    customerCrNumber?: string;
    customerAddress?: string;
    customerAddressTranslations?: Translations;

    createdAt: InvoiceSchema["createdAt"];
    issueDate: InvoiceSchema["issueDate"];
    invoiceProducts: {
      id: string;
      name: string;
      nameTranslations: Record<string, any> | null | undefined;
      originalUnitPrice: number;
      actualUnitPrice: number;
      originalPriceSubtotal: number;
      originalTaxSubtotal: number;
      actualPriceSubtotal: number;
      taxSubtotal: number;
      taxIsIncluded: boolean;
      quantity: number;
      barcode?: string;
      taxTypePercent?: number;
      taxTypeDisplayName?: string;
      taxTypeDisplayNameTranslations: Translations;
    }[];
    invoiceReceipts: {
      paymentMethod?: {
        id: string;
        name: string;
        nameTranslations: Translations;
      };
      type: "cash" | "card";
      amount: number;
      actualPaidAmount?: number;
    }[];
    callNumber: number;
    isAnonymousCustomer: boolean;

    // zatca
    zatcaIsEnabled?: boolean;
    zatcaQrCode?: string;
  };
  options: Omit<TerminalSchema, "printerNames">;
  posLogoUrl?: string | null;
}

let printWindow: Window | null = null;

export function usePrint() {
  const {
    getMemberName,
    getReceiptPaymentDetails,
    getContactInfo,
    getProductDetails,
  } = useMapIdsToData();
  const router = useRouter();
  const { silentPrint: silentPrintAPI } = usePrintHelper();
  const { setPrintData } = usePrintStorage();

  const origin = window.location.origin;
  const printBaseUrl = `${origin}/print`;
  const posStore = usePosStore();
  const { getProductPrices } = useProductStore();

  const buildPrintData = ({ invoice }: { invoice: InvoiceSchema }) => {
    const terminalStore = useTerminalStore();

    if (!posStore.pointOfSale) {
      throw new Error("Point of sale not found");
    }

    const contact = getContactInfo(invoice.customerId);

    const products: PrintParams["data"]["invoiceProducts"] =
      invoice.salesInvoiceProducts?.map((product) => {
        const enrichedProduct = getProductDetails(product.productId);

        const {
          originalUnitPrice,
          actualUnitPrice,
          originalPriceSubtotal,
          actualPriceSubtotal,
          originalTaxSubtotal,
          taxSubtotal,
        } = getProductPrices({
          originalUnitPrice: product.originalUnitPrice,
          actualUnitPrice: product.actualUnitPrice,
          quantity: product.quantity,
          taxPercent: product.taxPercent,
          taxIsIncluded: product.taxIsIncluded,
        });

        return {
          id: product.productId,
          name: enrichedProduct.productName,
          nameTranslations: enrichedProduct.productNameTranslations,
          quantity: product.quantity ?? 0,
          originalUnitPrice: originalUnitPrice || 0,
          actualUnitPrice: actualUnitPrice || 0,
          originalPriceSubtotal: originalPriceSubtotal || 0,
          originalTaxSubtotal: originalTaxSubtotal || 0,
          actualPriceSubtotal: actualPriceSubtotal || 0,
          taxSubtotal: taxSubtotal || 0,
          taxIsIncluded: product.taxIsIncluded,
          barcode: enrichedProduct.barcode,
          taxTypePercent: enrichedProduct.taxTypePercent
            ? enrichedProduct.taxTypePercent
            : undefined,
          taxTypeDisplayName: enrichedProduct.taxTypePercent
            ? enrichedProduct.taxTypeDisplayName
            : undefined,
          taxTypeDisplayNameTranslations: enrichedProduct.taxTypePercent
            ? enrichedProduct.taxTypeDisplayNameTranslations
            : undefined,
        };
      }) ?? [];

    const receipts: PrintParams["data"]["invoiceReceipts"] =
      invoice.salesReceiptInvoiceUsages?.map((receipt) => {
        const enrichedReceipt = getReceiptPaymentDetails(
          receipt.salesReceipt?.paymentMethodId
        );

        return {
          paymentMethod:
            receipt.salesReceipt?.paymentMethodId && enrichedReceipt
              ? {
                  id: receipt.salesReceipt!.paymentMethodId,
                  name: enrichedReceipt.displayName,
                  nameTranslations: enrichedReceipt.displayNameTranslations,
                }
              : undefined,

          type: !enrichedReceipt
            ? "cash"
            : enrichedReceipt.cashAccountId
              ? "card"
              : "cash",
          amount: receipt.amount ?? 0,
          actualPaidAmount: receipt.actualPaidAmount ?? null,
        };
      }) ?? [];

    const printData = {
      posLogoUrl: posStore.posLogoUrl,
      data: {
        // organization
        orgId: posStore.pointOfSale.orgId,
        orgDisplayName: posStore.pointOfSale.organization?.displayName,
        orgDisplayNameTranslations:
          posStore.pointOfSale.organization?.displayNameTranslations,
        orgTaxNumber: posStore.pointOfSale.organization?.taxNumber,
        orgPhone: posStore.pointOfSale.organization?.phone,
        orgAddress: posStore.pointOfSale.organization?.address,
        orgAddressTranslations:
          posStore.pointOfSale.organization?.addressTranslations,
        orgCountry: posStore.pointOfSale.organization?.country,
        posSettings: posStore.pointOfSale.organization?.posSettings,

        // store
        branchName: posStore.pointOfSale.inventoryStore?.name,
        branchNameTranslations:
          posStore.pointOfSale.inventoryStore?.nameTranslations,
        branchAddress:
          posStore.pointOfSale.inventoryStore?.address ??
          posStore.pointOfSale.organization?.address,
        branchAddressTranslations:
          posStore.pointOfSale.inventoryStore?.addressTranslations ??
          posStore.pointOfSale.organization?.addressTranslations,

        // invoice
        callNumber: invoice.callNumber ?? 0,
        isAnonymousCustomer: invoice.isAnonymousCustomer ?? false,
        invoiceReference: invoice.reference,
        invoiceProducts: products,
        invoiceReceipts: receipts,

        createdByName: getMemberName(invoice.memberId),

        createdAt: invoice.createdAt,
        issueDate: invoice.issueDate,

        customerName: contact.contactName,
        customerNameTranslations: contact.contactNameTranslations,
        customerTaxNumber: contact.contactTaxNumber,
        customerCrNumber: contact.contactCrNumber,
        customerAddress: contact.contactAddress ?? undefined,
        customerAddressTranslations:
          contact.contactAddressTranslations ?? undefined,

        // zatca
        zatcaIsEnabled: posStore.zatcaSettings.enabled,
        zatcaQrCode:
          posStore.zatcaSettings.isProduction === true
            ? invoice.productionZatcaInvoice?.qr
            : invoice.simulationZatcaInvoice?.qr,
      },
      options: terminalStore.options,
    };

    return printData;
  };

  const silentPrint = async ({ invoice }: { invoice: InvoiceSchema }) =>
    silentPrintAPI({
      printInvoice: buildPrintData({ invoice }),
      printBaseUrl,
    });

  const print = ({ invoice }: { invoice: InvoiceSchema }) => {
    const data = buildPrintData({ invoice });
    setPrintData(data);

    const resolvedRoute = router.resolve({
      name: "print",
    });

    try {
      if (
        !printWindow ||
        printWindow.closed ||
        printWindow.location.href == null
      ) {
        // Open new tab if it doesn't exist or was closed
        printWindow = window.open(resolvedRoute.href, "printTab");
      } else {
        // Instead of changing location directly, use history.pushState and then reload
        // This prevents affecting the parent window
        printWindow.focus();

        // Use postMessage to navigate safely
        printWindow.postMessage(
          { type: "navigate", url: resolvedRoute.href },
          "*"
        );
      }
    } catch {
      // Handle any cross-origin errors by opening a new window
      printWindow = window.open(resolvedRoute.href, "printTab");
    }
  };

  const getTestPrintData = () => {
    return {
      url: printBaseUrl,
      posLogoUrl: posStore.posLogoUrl,
    };
  };

  return {
    silentPrint,
    print,
    getTestPrintData,
  };
}
